import React, { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

/**
 * The four things Medical Emergency §5.1 requires Melio to hold before any
 * clinical contact.
 *
 *   §5.1(1) a UK residential address
 *   §5.1(2) a mobile number to call back on if a video call fails
 *   §5.1(3) an emergency contact — name, number, AND the member's consent to
 *           contact that person in a Medical Emergency
 *   §5.1(4) the name and practice of their NHS GP
 *
 * Only the first two existed, and neither was verified. The emergency contact was
 * dead code in `src/pages/Profile.tsx`, a file nothing imports and no route
 * reaches; consent to contact did not exist in any form; and the GP fields lived
 * only in a booking flow whose entry point is not routed.
 *
 * §5.2 is why the copy says what it says: "Members are told at registration that
 * this information is held so that an ambulance can be directed to them if they
 * become unwell during a consultation." Asking someone for a next of kin without
 * saying why reads as an administrative formality, and gets an administrative
 * answer.
 *
 * The consent is a separate confirmation rather than being implied by supplying
 * the number. §5.1(3) asks for both, and §5.6(2) has the clinician contacting that
 * person during an emergency — Melio should know before then whether the member
 * agreed to it.
 */

interface EmergencyDetailsForm {
  emergencyContactName?: string;
  emergencyContactRelationship?: string;
  emergencyContactPhone?: string;
  emergencyContactConsent?: boolean;
  gpPracticeName?: string;
  gpPracticeAddress?: string;
  mobilePhone?: string;
}

export default function EmergencyDetails() {
  const { currentUser } = useAuth();
  const [form, setForm] = useState<EmergencyDetailsForm>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      const snap = await getDoc(doc(db, 'users', currentUser.uid));
      const data = snap.exists() ? (snap.data() as Record<string, any>) : {};
      setForm({
        emergencyContactName: data.emergencyContactName || '',
        emergencyContactRelationship: data.emergencyContactRelationship || '',
        emergencyContactPhone: data.emergencyContactPhone || '',
        emergencyContactConsent: data.emergencyContactConsent === true,
        gpPracticeName: data.gpPracticeName || '',
        gpPracticeAddress: data.gpPracticeAddress || '',
        mobilePhone: data.mobilePhone || data.phone || '',
      });
    } catch {
      setError('Could not load your details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!currentUser) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        ...form,
        // Stamped so a clinician can tell a consent given today from one given at
        // registration two years ago and never revisited.
        emergencyContactConsentAt: form.emergencyContactConsent ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSaved(true);
    } catch {
      setError('Could not save that. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) return null;

  const field = (
    key: keyof EmergencyDetailsForm,
    label: string,
    help?: string,
    type: string = 'text',
  ) => (
    <label className="block">
      <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
        {label}
      </span>
      <input
        type={type}
        value={(form[key] as string) || ''}
        onChange={(e) => { setForm({ ...form, [key]: e.target.value }); setSaved(false); }}
        className="w-full rounded-xl border px-4 py-3 text-sm"
        style={{
          borderColor: 'var(--melio-border)',
          backgroundColor: 'var(--b-bone)',
          color: 'var(--melio-charcoal)',
        }}
      />
      {help && (
        <span className="block text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
          {help}
        </span>
      )}
    </label>
  );

  return (
    <div>
      {/* §5.2 — said plainly, because the reason is what makes the answer useful. */}
      <p className="text-sm mb-5" style={{ color: 'var(--melio-text-muted)' }}>
        Melio holds this so an ambulance can be directed to you if you become unwell during an
        appointment, and so we can reach someone if we cannot reach you. Please keep it current.
      </p>

      {error && (
        <div
          className="rounded-xl border px-4 py-3 mb-4 text-sm"
          style={{ borderColor: 'rgba(139, 61, 43,0.4)' }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>Loading…</p>
      ) : (
        <div className="space-y-4">
          {field(
            'mobilePhone',
            'Mobile number',
            'So a clinician can call you back if a video appointment drops.',
            'tel',
          )}

          <div className="pt-2">
            <p className="text-sm font-medium mb-3" style={{ color: 'var(--melio-charcoal)' }}>
              Your emergency contact
            </p>
            <div className="space-y-4">
              {field('emergencyContactName', 'Name')}
              {field('emergencyContactRelationship', 'How you know them', 'Partner, parent, friend.')}
              {field('emergencyContactPhone', 'Their phone number', undefined, 'tel')}

              <label className="flex items-start gap-3 text-sm" style={{ color: 'var(--melio-charcoal)' }}>
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.emergencyContactConsent === true}
                  onChange={(e) => {
                    setForm({ ...form, emergencyContactConsent: e.target.checked });
                    setSaved(false);
                  }}
                />
                <span>
                  Melio may contact this person if I have a medical emergency during an
                  appointment, or if Melio believes I am at immediate risk and cannot reach me.
                </span>
              </label>
            </div>
          </div>

          <div className="pt-2">
            <p className="text-sm font-medium mb-3" style={{ color: 'var(--melio-charcoal)' }}>
              Your NHS GP
            </p>
            <div className="space-y-4">
              {field('gpPracticeName', 'Practice name')}
              {field(
                'gpPracticeAddress',
                'Practice town or postcode',
                'Melio works alongside your GP and writes to them when something needs their '
                + 'attention.',
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="px-4 py-2.5 rounded-lg text-sm font-medium disabled:opacity-40"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {saved && (
              <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>Saved.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
